import * as React from "react"

import * as SliderPrimitive from "@radix-ui/react-slider"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

const sliderVariants = cva([
  "relative flex w-full touch-none select-none items-center",
  "data-[disabled]:opacity-50",
])

const sliderTrackVariants = cva([
  "relative h-1 w-full grow overflow-hidden rounded-full",
  "bg-surface-1",
])

const sliderRangeVariants = cva([
  "absolute h-full rounded-full",
  "bg-coval-contrast",
])

const sliderThumbVariants = cva([
  "block size-4 shrink-0 rounded-full",
  "not-data-[disabled]:cursor-grab not-data-[disabled]:active:cursor-grabbing",
  // The thumb is the control, so its edge holds 3:1 against the track, the
  // filled range, and the page, as the other selection controls' edges do.
  "border border-coval-border-control bg-white",
  "shadow-sm",
  "transition-colors duration-150",
  "not-data-[disabled]:hover:bg-surface-default-hover",
])

export type SliderProps = Omit<
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
  "aria-label" | "aria-labelledby"
> &
  AccessibleName &
  VariantProps<typeof sliderVariants> & {
    /**
     * Names each thumb in order, replacing the names derived from the
     * slider's own name ("<name> minimum" and "<name> maximum" on a range).
     */
    thumbLabels?: string[]
  }

/** The word that tells one thumb of a multi-thumb slider from the others. */
function thumbSuffix(index: number, count: number): string | undefined {
  if (count < 2) {
    return undefined
  }
  if (count === 2) {
    return index === 0 ? "minimum" : "maximum"
  }
  return `${index + 1} of ${count}`
}

/**
 * The slider's name belongs on its thumbs: each thumb carries
 * `role="slider"`, while the root is a plain span with no role to name.
 */
const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      defaultValue,
      thumbLabels,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const suffixIdPrefix = React.useId()
    const values = props.value ?? defaultValue ?? [0]

    return (
      <SliderPrimitive.Root
        ref={ref}
        defaultValue={defaultValue ?? [0]}
        className={cn(sliderVariants(), className)}
        {...props}
      >
        <SliderPrimitive.Track className={cn(sliderTrackVariants())}>
          <SliderPrimitive.Range className={cn(sliderRangeVariants())} />
        </SliderPrimitive.Track>
        {values.map((_, i) => {
          const override = thumbLabels?.[i]
          const suffix = thumbSuffix(i, values.length)
          const suffixId = `${suffixIdPrefix}-thumb-${i}`
          // A referenced name keeps its reference, and the thumb's own word
          // joins it from a hidden span so the two read as one name.
          const labelledBy =
            !override && ariaLabelledBy
              ? suffix
                ? `${ariaLabelledBy} ${suffixId}`
                : ariaLabelledBy
              : undefined
          const label =
            override ??
            (labelledBy
              ? undefined
              : suffix && ariaLabel
                ? `${ariaLabel} ${suffix}`
                : ariaLabel)

          return (
            <React.Fragment key={i}>
              {labelledBy && suffix && (
                <span id={suffixId} hidden>
                  {suffix}
                </span>
              )}
              <SliderPrimitive.Thumb
                aria-label={label}
                aria-labelledby={labelledBy}
                className={cn(sliderThumbVariants())}
              />
            </React.Fragment>
          )
        })}
      </SliderPrimitive.Root>
    )
  },
)
Slider.displayName = SliderPrimitive.Root.displayName

export {
  Slider,
  sliderVariants,
  sliderTrackVariants,
  sliderRangeVariants,
  sliderThumbVariants,
}
